// ============================================
// COMPANYDIAGNOSTICFORMSPAGE.JSX - Company Diagnostic Forms Sub-Page
// ============================================

import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetCompanyByIdQuery,
  useGetCompanyMembersQuery,
  useGetCompanyDiagnosticAssignmentsQuery,
  useCancelDiagnosticAssignmentMutation,
  useGetDiagnosticSubmissionByIdQuery,
} from "../../../services/api";
import DiagnosticSection from "./DiagnosticSection";
import { IconCircle } from "./components/Icons";
import MetricsGrid from "./components/MetricsGrid";
import DiagnosticAssignmentsTable from "./components/DiagnosticAssignmentsTable";
import "./CompanyDetails.css";

/**
 * CompanyDiagnosticFormsPage Component
 * Sub-page for managing company diagnostic forms and assignments
 */
export default function CompanyDiagnosticFormsPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  // ==================== API QUERIES ====================
  const { data: companyData } = useGetCompanyByIdQuery(id);
  const companyName = companyData?.name || "Loading...";

  // Get employees for DiagnosticSection
  const { data: membersData } = useGetCompanyMembersQuery({
    id,
    page: 1,
    limit: 100, // Get all employees for assignment dropdown
  });
  const employees = membersData?.members || [];

  // Get diagnostic assignments
  const { data: assignmentsData, refetch: refetchAssignments } =
    useGetCompanyDiagnosticAssignmentsQuery({ companyId: id });
  const diagnosticAssignments = assignmentsData?.assignments || [];

  // ==================== MUTATIONS ====================
  const [cancelDiagnosticAssignment] = useCancelDiagnosticAssignmentMutation();

  // ==================== UI STATE ====================
  const [answersModal, setAnswersModal] = useState({ isOpen: false, assignment: null });

  // Fetch submission when answers modal is open
  const submissionId = answersModal.assignment?.submission?._id || answersModal.assignment?.submission;
  const { data: submissionData, isLoading: submissionLoading } = useGetDiagnosticSubmissionByIdQuery(
    submissionId,
    { skip: !answersModal.isOpen || !submissionId }
  );

  // ==================== HANDLERS ====================
  const handleCancelDiagnosticAssignment = async (assignmentId) => {
    if (!window.confirm("Are you sure you want to cancel this assignment?")) return;
    try {
      await cancelDiagnosticAssignment(assignmentId).unwrap();
      refetchAssignments();
    } catch (error) {
      console.error("Failed to cancel assignment:", error);
    }
  };

  const handleOpenAnswersModal = (assignment) => {
    setAnswersModal({ isOpen: true, assignment });
  };

  const handleCloseAnswersModal = () => {
    setAnswersModal({ isOpen: false, assignment: null });
  };

  // Get status badge style
  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: "#fef3c7", color: "#d97706", label: "Pending" },
      in_progress: { bg: "#dbeafe", color: "#2563eb", label: "In Progress" },
      completed: { bg: "#dcfce7", color: "#16a34a", label: "Completed" },
      expired: { bg: "#fee2e2", color: "#dc2626", label: "Expired" },
    };
    return styles[status] || styles.pending;
  };

  // Format answer value based on question type
  const formatAnswerValue = (answer) => {
    if (answer.value === null || answer.value === undefined || answer.value === "") {
      return <span className="cd-answer-empty">No answer provided</span>;
    }
    switch (answer.questionType) {
      case "single_choice":
        return answer.valueLabel || answer.value;
      case "multi_choice":
        if (answer.valueLabels && answer.valueLabels.length > 0) {
          return answer.valueLabels.join(", ");
        }
        if (Array.isArray(answer.value)) {
          return answer.value.join(", ");
        }
        return answer.value;
      case "rating_scale":
        return `${answer.value} / 5`;
      case "checkbox":
        return answer.value ? "Yes" : "No";
      default:
        return answer.value;
    }
  };

  // ==================== METRICS ====================
  const metrics = [
    { icon: <IconCircle />, label: "Diagnostic Assignments", value: diagnosticAssignments.length.toString() },
  ];

  // ==================== RENDER ====================
  return (
    <>
      <div className="cd-company-details">
        <div className="welcome" onClick={() => navigate(`/admin/companies/${id}`)}>
          ← Back to {companyName}
        </div>
        <h1 className="username">{companyName} - Diagnostic Forms</h1>

        <MetricsGrid metrics={metrics} />

        <DiagnosticSection companyId={id} employees={employees} />

        <DiagnosticAssignmentsTable
          assignments={diagnosticAssignments}
          onCancelAssignment={handleCancelDiagnosticAssignment}
          onViewAnswers={handleOpenAnswersModal}
        />
      </div>

      {/* Diagnostic Answers Modal */}
      {answersModal.isOpen && answersModal.assignment && (
        <div className="cd-modal-overlay" onClick={handleCloseAnswersModal}>
          <div className="cd-modal-content cd-modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="cd-modal-header">
              <h2 className="cd-modal-title">Diagnostic Answers</h2>
              <button className="cd-modal-close" onClick={handleCloseAnswersModal}>
                ✕
              </button>
            </div>

            <div className="cd-modal-body">
              {/* Assignment Info */}
              <div className="cd-answers-info">
                <div className="cd-answers-info-row">
                  <span className="cd-answers-label">Form:</span>
                  <span className="cd-answers-value">
                    {answersModal.assignment.template?.title || "—"}
                  </span>
                </div>
                <div className="cd-answers-info-row">
                  <span className="cd-answers-label">Employee:</span>
                  <span className="cd-answers-value">
                    {answersModal.assignment.assignee?.firstName}{" "}
                    {answersModal.assignment.assignee?.lastName}
                  </span>
                </div>
                <div className="cd-answers-info-row">
                  <span className="cd-answers-label">Type:</span>
                  <span className="cd-answers-value">
                    {answersModal.assignment.evaluationType === "self"
                      ? "Self Evaluation"
                      : `Colleague Evaluation of ${answersModal.assignment.target?.firstName} ${answersModal.assignment.target?.lastName}`}
                  </span>
                </div>
                <div className="cd-answers-info-row">
                  <span className="cd-answers-label">Status:</span>
                  <span className="cd-answers-value">
                    {getStatusBadge(answersModal.assignment.status).label}
                  </span>
                </div>
                {submissionData?.submittedAt && (
                  <div className="cd-answers-info-row">
                    <span className="cd-answers-label">Submitted:</span>
                    <span className="cd-answers-value">
                      {new Date(submissionData.submittedAt).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                )}
              </div>

              {/* Loading State */}
              {submissionLoading && (
                <div className="cd-loading">Loading answers...</div>
              )}

              {/* No Submission */}
              {!submissionLoading && !submissionData && (
                <div className="cd-empty-state">
                  <p>No submission data available.</p>
                </div>
              )}

              {/* Answers List */}
              {!submissionLoading && submissionData && submissionData.answers && (
                <div className="cd-answers-list">
                  {submissionData.answers.map((answer, index) => (
                    <div key={answer._id || index} className="cd-answer-item">
                      <div className="cd-answer-question">
                        <span className="cd-answer-number">Q{answer.questionIndex + 1}.</span>
                        {answer.questionText}
                      </div>
                      <div className="cd-answer-response">
                        <span className="cd-answer-prefix">A:</span>
                        {formatAnswerValue(answer)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="cd-modal-footer">
              <button
                className="cd-modal-btn cd-modal-btn-cancel"
                onClick={handleCloseAnswersModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
